import { useState, useRef, useEffect } from "react";
import { colors, font, maxWidth } from "../styles/theme";
import { projects } from "../data/projects";
import Lightbox from "../components/Lightbox";
import SmartImage, { preloadImages } from "../components/SmartImage";
import useMediaQuery from "../hooks/useMediaQuery";

const SHOWN_LIMIT = 6; // сколько объектов показывать до «Показать ещё» (ПК и мобильный)

// Блок «Реализованные объекты» — портфолио с галереями фото.
export default function PortfolioSection() {
  const [box, setBox] = useState(null); // { photos, title } для лайтбокса
  const [expanded, setExpanded] = useState(false);

  // Показываем первые 6, остальные — по кнопке «Показать ещё».
  const limited = !expanded;
  const shown = limited ? projects.slice(0, SHOWN_LIMIT) : projects;
  const hiddenCount = projects.length - SHOWN_LIMIT;

  return (
    <section id="objects" className="section-pad" style={{ position: "relative", background: colors.bgLight, padding: "70px 22px", overflow: "hidden" }}>
      <div style={{ position: "relative", zIndex: 1, maxWidth, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, flexWrap: "wrap", marginBottom: 40 }}>
          <div>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontFamily: font.display,
                fontWeight: 600,
                fontSize: 12,
                letterSpacing: ".16em",
                textTransform: "uppercase",
                color: colors.red,
              }}
            >
              <span style={{ width: 26, height: 2, background: colors.red, display: "inline-block" }} />
              Наши работы
            </span>
            <h2 style={{ fontFamily: font.display, fontWeight: 700, fontSize: "clamp(2rem,4vw,3.2rem)", textTransform: "uppercase", margin: "14px 0 0", color: colors.ink }}>
              Реализованные объекты
            </h2>
          </div>
          <p style={{ maxWidth: 440, fontSize: 16, lineHeight: 1.55, color: "#5b6470", margin: 0 }}>
            Более 500 сданных объектов — от квартир и коттеджей до производств. Наведите на фото, чтобы посмотреть кадры,
            и нажмите — откроется галерея объекта.
          </p>
        </div>

        <div className="portfolio-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 22 }}>
          {shown.map((p) => (
            <Card key={p.id} project={p} onOpen={(start = 0) => setBox({ photos: p.photos, title: p.title, start })} />
          ))}
        </div>

        {limited && hiddenCount > 0 && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 26 }}>
            <button
              onClick={() => setExpanded(true)}
              style={{
                background: "#fff",
                color: colors.ink,
                border: "1px solid #e6e9ee",
                fontFamily: font.display,
                fontWeight: 600,
                fontSize: 14,
                letterSpacing: ".04em",
                textTransform: "uppercase",
                padding: "15px 32px",
                borderRadius: 11,
                cursor: "pointer",
                transition: "border-color .18s, color .18s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.red;
                e.currentTarget.style.color = colors.red;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e6e9ee";
                e.currentTarget.style.color = colors.ink;
              }}
            >
              Показать ещё {hiddenCount}
            </button>
          </div>
        )}
      </div>

      {box && <Lightbox photos={box.photos} title={box.title} start={box.start || 0} onClose={() => setBox(null)} />}
    </section>
  );
}

function Card({ project, onOpen }) {
  const photos = project.photos;
  const isMobile = useMediaQuery("(max-width: 760px)");

  // ПК: 3 превью-кадра для зон (лево/центр/право).
  const previews = [photos[0], photos[Math.floor(photos.length / 2)] || photos[0], photos[photos.length - 1] || photos[0]];
  const [zone, setZone] = useState(0);

  // Мобильный: свайп листает ВСЕ фото прямо в карточке.
  const [photoIdx, setPhotoIdx] = useState(0);
  const touchX = useRef(null);
  const touchY = useRef(null);

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    setZone(x < 1 / 3 ? 0 : x < 2 / 3 ? 1 : 2);
  };

  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
    touchY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    const dy = e.changedTouches[0].clientY - touchY.current;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      // Горизонтальный свайп — листаем фото в карточке.
      setPhotoIdx((i) => (dx < 0 ? (i + 1) % photos.length : (i - 1 + photos.length) % photos.length));
    } else if (Math.abs(dx) < 12 && Math.abs(dy) < 12) {
      // Тап (почти без движения) — открываем галерею с текущего фото.
      onOpen(photoIdx);
    }
    touchX.current = null;
    touchY.current = null;
  };

  // На мобильном показываем текущее фото свайпа; на ПК — превью по зоне.
  const shownSrc = isMobile ? photos[photoIdx] : null;

  // Предзагрузка соседних кадров для плавного свайпа на мобильном.
  useEffect(() => {
    if (!isMobile) return;
    const n = photos.length;
    preloadImages([photos[(photoIdx + 1) % n], photos[(photoIdx - 1 + n) % n]]);
  }, [isMobile, photoIdx, photos]);

  const specs = [
    project.area && project.area !== "—" ? { label: "Площадь", value: project.area } : null,
    { label: "Тип", value: project.type },
  ].filter(Boolean);

  return (
    <article
      style={{
        background: "#fff",
        border: "1px solid #e6e9ee",
        borderRadius: 18,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Фото-часть: ПК — смена кадра по зоне + клик = лайтбокс; мобильный — свайп по всем фото */}
      <div
        onMouseMove={isMobile ? undefined : onMove}
        onMouseLeave={isMobile ? undefined : () => setZone(0)}
        onClick={isMobile ? undefined : () => onOpen(0)}
        onTouchStart={isMobile ? onTouchStart : undefined}
        onTouchEnd={isMobile ? onTouchEnd : undefined}
        title={isMobile ? undefined : "Открыть галерею"}
        style={{ position: "relative", height: 230, overflow: "hidden", cursor: isMobile ? "default" : "zoom-in", touchAction: "pan-y" }}
      >
        {/* Мобильный: одно фото (текущее по свайпу) со скелетоном */}
        {isMobile && (
          <SmartImage
            src={shownSrc}
            alt={project.title}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          />
        )}
        {/* ПК: 3 превью с кроссфейдом по зоне */}
        {!isMobile && previews.map((src, i) => (
          <img
            key={i}
            src={src}
            alt={project.title}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: zone === i ? 1 : 0,
              transition: "opacity .25s ease",
            }}
          />
        ))}

        {/* категория */}
        <span
          style={{
            position: "absolute",
            top: 14,
            left: 14,
            background: "rgba(227,6,19,.92)",
            color: "#fff",
            fontFamily: font.display,
            fontWeight: 600,
            fontSize: 11,
            letterSpacing: ".06em",
            textTransform: "uppercase",
            padding: "7px 13px",
            borderRadius: 8,
            pointerEvents: "none",
          }}
        >
          {project.category}
        </span>

        {/* счётчик фото */}
        <span
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            background: "rgba(0,0,0,.4)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            color: "#fff",
            fontFamily: font.body,
            fontSize: 12,
            padding: "5px 10px",
            borderRadius: 8,
            pointerEvents: "none",
          }}
        >
          {photos.length} фото
        </span>

        {/* индикатор: ПК — 3 зоны; мобильный — точки по числу фото */}
        <div style={{ position: "absolute", bottom: 14, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 6, pointerEvents: "none", flexWrap: "wrap", padding: "0 14px" }}>
          {isMobile
            ? photos.map((_, i) => (
                <span
                  key={i}
                  style={{
                    width: photoIdx === i ? 18 : 6,
                    height: 5,
                    borderRadius: 3,
                    background: photoIdx === i ? "#fff" : "rgba(255,255,255,.5)",
                    transition: "width .2s",
                  }}
                />
              ))
            : [0, 1, 2].map((i) => (
                <span
                  key={i}
                  style={{
                    width: zone === i ? 22 : 7,
                    height: 5,
                    borderRadius: 3,
                    background: zone === i ? "#fff" : "rgba(255,255,255,.55)",
                    transition: "width .2s",
                  }}
                />
              ))}
        </div>
      </div>

      {/* Текст */}
      <div style={{ padding: "22px 22px 24px", display: "flex", flexDirection: "column", flex: 1 }}>
        <h3 style={{ fontFamily: font.display, fontWeight: 700, fontSize: 21, textTransform: "uppercase", margin: 0, color: colors.ink, lineHeight: 1.15 }}>
          {project.title}
        </h3>
        <p style={{ margin: "12px 0 0", fontSize: 14.5, lineHeight: 1.55, color: "#5b6470", flex: 1 }}>{project.summary}</p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 18, marginTop: 18, paddingTop: 18, borderTop: "1px solid #eef0f3" }}>
          {specs.map((s) => (
            <div key={s.label}>
              <div style={{ fontFamily: font.body, fontSize: 11, letterSpacing: ".05em", textTransform: "uppercase", color: "#9aa2ac" }}>{s.label}</div>
              <div style={{ fontFamily: font.display, fontWeight: 600, fontSize: 14, color: colors.ink, marginTop: 4 }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}
